import { useId } from 'react';
import { Trash2 } from 'lucide-react';

import { FileUpload, type UploadResult } from '@/components/FileUpload';

import { AltTextField } from './AltTextField';
import { promptImageFileName, promptImageSrc } from './promptContentUnits';
import type { EditableQuestionImage } from './editorTypes';
import styles from './QuestionImagesEditor.module.css';

interface QuestionImagesEditorProps {
  // What the images belong to — "prompt", "hint", "solution" — used in the
  // controls' accessible names so three lists on one screen stay distinct.
  context: string;
  description: string;
  disabled?: boolean;
  heading: string;
  images: EditableQuestionImage[];
  onImageChange: (id: string, updates: Partial<EditableQuestionImage>) => void;
  onRemoveImage: (id: string) => void;
  onUploadComplete: (result: UploadResult) => void;
  // Each upload's start and end, so the dialog saves only once its images are in.
  onUploadEnd?: () => void;
  onUploadStart?: () => void;
}

// Matches the image content unit's sizes, so a figure in a question and a figure
// in a chapter offer the same choices.
const IMAGE_SIZES: Array<{ label: string; value: number }> = [
  { label: 'Small', value: 1 },
  { label: 'Medium', value: 2 },
  { label: 'Large', value: 3 },
];

// A stored size is whatever was authored — 1/2/3 or SMALL/MEDIUM/LARGE — so it
// is normalized for the select rather than assumed numeric.
function imageSizeValue(imageSize: number | string | undefined) {
  const normalized =
    typeof imageSize === 'number' ? String(imageSize) : imageSize?.toUpperCase();

  if (normalized === '1' || normalized === 'SMALL') {
    return 1;
  }

  if (normalized === '3' || normalized === 'LARGE') {
    return 3;
  }

  return 2;
}

/**
 * The image list for one content section of a question — the prompt, the hint,
 * or the worked solution.
 *
 * All three are `ContentSection`s in the proto and can hold image units, so they
 * share this rather than each growing its own. Hints and solutions previously
 * had no image editor at all, which meant an authored image in one was dropped
 * the next time the question was saved.
 */
export function QuestionImagesEditor({
  context,
  description,
  disabled = false,
  heading,
  images,
  onImageChange,
  onRemoveImage,
  onUploadComplete,
  onUploadEnd,
  onUploadStart,
}: QuestionImagesEditorProps) {
  const headingId = useId();

  return (
    <section aria-labelledby={headingId} className={styles.section}>
      <div className={styles.header}>
        <h4 className={styles.title} id={headingId}>
          {heading}
        </h4>
        <p className={styles.hint}>{description}</p>
      </div>

      {images.length > 0 ? (
        <ul className={styles.list}>
          {images.map((image, index) => (
            <li className={styles.row} key={image.id}>
              <img alt="" className={styles.thumbnail} src={promptImageSrc(image)} />
              <div className={styles.meta}>
                {/* The file name, not the URL: a Firebase download URL is a wall
                    of encoded path and token that identifies nothing. */}
                <span className={styles.name} title={promptImageSrc(image)}>
                  {promptImageFileName(image)}
                </span>
                <div className={styles.controls}>
                  <label className={styles.control}>
                    <span className={styles.controlLabel}>Size</span>
                    <select
                      className={styles.select}
                      disabled={disabled}
                      onChange={(event) => {
                        onImageChange(image.id, {
                          imageSize: Number(event.target.value),
                        });
                      }}
                      value={imageSizeValue(image.imageSize)}
                    >
                      {IMAGE_SIZES.map((size) => (
                        <option key={size.value} value={size.value}>
                          {size.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className={[styles.control, styles.caption].join(' ')}>
                    <span className={styles.controlLabel}>Caption</span>
                    <input
                      className={styles.input}
                      disabled={disabled}
                      onChange={(event) => {
                        onImageChange(image.id, { caption: event.target.value });
                      }}
                      placeholder="Shown under the image"
                      type="text"
                      value={image.caption ?? ''}
                    />
                  </label>
                </div>
              </div>
              <button
                aria-label={`Remove ${context} image ${index + 1}`}
                className={styles.delete}
                disabled={disabled}
                onClick={() => {
                  onRemoveImage(image.id);
                }}
                type="button"
              >
                <Trash2 aria-hidden="true" size={16} strokeWidth={2} />
              </button>
              <div className={styles.altText}>
                <AltTextField
                  disabled={disabled}
                  onChange={(altText) => {
                    onImageChange(image.id, { altText });
                  }}
                  value={image.altText}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      <div className={styles.upload}>
        <FileUpload
          accept="image/*"
          buttonLabel={
            images.length > 0 ? 'Add another image' : `Upload ${context} image`
          }
          disabled={disabled}
          multiple
          onUploadComplete={onUploadComplete}
          onUploadEnd={onUploadEnd}
          onUploadStart={onUploadStart}
          storagePath="images/uploads"
        />
      </div>
    </section>
  );
}
